import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Calendar, Car, Check, FileText, Send, User, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { normalizeLocalization } from '../lib/localization';

const copy = {
  pt: {
    loading: 'Carregando orçamento...',
    notFoundTitle: 'Orçamento não encontrado',
    notFoundBody: 'Este orçamento pode ter expirado ou o link é inválido.',
    notFoundHelp: 'Entre em contato com a oficina para solicitar um novo orçamento.',
    title: 'Orçamento Digital',
    subtitle: 'Revise e aprove o orçamento do seu veículo',
    customer: 'Cliente',
    vehicle: 'Veículo',
    version: 'Versão',
    items: 'Itens do Orçamento',
    service: 'Serviço',
    part: 'Peça',
    subtotal: 'Subtotal',
    discount: 'Desconto',
    total: 'Total',
    notes: 'Observações',
    decision: 'Sua Decisão',
    name: 'Seu nome *',
    namePlaceholder: 'Digite seu nome completo',
    customerNotes: 'Observações (opcional)',
    notesPlaceholder: 'Adicione comentários ou solicite alterações...',
    approve: 'Aprovar',
    change: 'Solicitar Alteração',
    reject: 'Rejeitar',
    validity: 'Este orçamento é válido por 14 dias a partir do envio.',
    product: 'MecSync - Sistema de Gestão de Oficinas',
    missingName: 'Por favor, informe seu nome',
    loadError: 'Erro ao carregar orçamento',
    decisionTitle: 'Decisão registrada',
    decisionBody: (decision) => `Seu orçamento foi ${decision}. A oficina já pode acompanhar essa atualização no MecSync.`,
    decisionSuccess: (decision) => `Orçamento ${decision} com sucesso!`,
    decisionError: 'Erro ao registrar decisão',
    decisions: {
      approved: 'aprovado',
      rejected: 'rejeitado',
      change_requested: 'solicitou alterações',
    },
  },
  es: {
    loading: 'Cargando presupuesto...',
    notFoundTitle: 'Presupuesto no encontrado',
    notFoundBody: 'Este presupuesto puede haber caducado o el enlace no es válido.',
    notFoundHelp: 'Ponte en contacto con el taller para solicitar un nuevo presupuesto.',
    title: 'Presupuesto Digital',
    subtitle: 'Revisa y aprueba el presupuesto de tu vehículo',
    customer: 'Cliente',
    vehicle: 'Vehículo',
    version: 'Versión',
    items: 'Conceptos del Presupuesto',
    service: 'Servicio',
    part: 'Pieza',
    subtotal: 'Subtotal',
    discount: 'Descuento',
    total: 'Total',
    notes: 'Observaciones',
    decision: 'Tu Decisión',
    name: 'Tu nombre *',
    namePlaceholder: 'Escribe tu nombre completo',
    customerNotes: 'Observaciones (opcional)',
    notesPlaceholder: 'Añade comentarios o solicita cambios...',
    approve: 'Aprobar',
    change: 'Solicitar Cambios',
    reject: 'Rechazar',
    validity: 'Este presupuesto es válido durante 14 días desde su envío.',
    product: 'MecSync - Sistema de Gestión de Talleres',
    missingName: 'Por favor, informa tu nombre',
    loadError: 'Error al cargar el presupuesto',
    decisionTitle: 'Decisión registrada',
    decisionBody: (decision) => `Tu presupuesto fue ${decision}. El taller ya puede seguir esta actualización en MecSync.`,
    decisionSuccess: (decision) => `Presupuesto ${decision} correctamente.`,
    decisionError: 'Error al registrar la decisión',
    decisions: {
      approved: 'aprobado',
      rejected: 'rechazado',
      change_requested: 'con cambios solicitados',
    },
  },
};

function getQuoteLocalization(quote) {
  return normalizeLocalization(quote?.localization || {});
}

function getCopy(settings) {
  return settings.language?.startsWith('es') || settings.locale?.startsWith('es') ? copy.es : copy.pt;
}

function formatQuoteCurrency(value, settings) {
  return new Intl.NumberFormat(settings.locale, {
    style: 'currency',
    currency: settings.currency,
  }).format(Number(value || 0));
}

export default function PublicQuote() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [decisionRecorded, setDecisionRecorded] = useState(null);

  const settings = useMemo(() => getQuoteLocalization(quote), [quote]);
  const text = useMemo(() => getCopy(settings), [settings]);
  const money = useCallback((value) => formatQuoteCurrency(value, settings), [settings]);

  const loadQuote = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_public_quote', {
        p_token: token,
      });

      if (error) throw error;
      if (!data) {
        toast.error(text.notFoundTitle);
        return;
      }

      setQuote(data);
    } catch (error) {
      console.error('Erro ao carregar orçamento:', error);
      toast.error(text.loadError);
    } finally {
      setLoading(false);
    }
  }, [token, text.loadError, text.notFoundTitle]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  const handleDecision = async (decision) => {
    if (!customerName.trim()) {
      toast.error(text.missingName);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('record_quote_decision', {
        p_token: token,
        p_decision: decision,
        p_customer_name: customerName,
        p_customer_note: customerNote,
        p_user_agent: navigator.userAgent,
      });

      if (error) throw error;

      const decisionLabel = text.decisions[decision];
      toast.success(text.decisionSuccess(decisionLabel));
      setDecisionRecorded(decisionLabel);
    } catch (error) {
      console.error('Erro ao registrar decisão:', error);
      toast.error(`${text.decisionError}: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-800">
        <div className="text-center text-white">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
          <p>{text.loading}</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-800">
        <div className="max-w-md p-8 text-center text-white">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h1 className="mb-2 text-2xl font-bold">{text.notFoundTitle}</h1>
          <p className="text-zinc-400">{text.notFoundBody}</p>
          <p className="mt-4 text-sm text-zinc-500">{text.notFoundHelp}</p>
        </div>
      </div>
    );
  }

  const currentVersion = quote.versions?.[0] || null;

  if (decisionRecorded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-800 px-4">
        <div className="max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white backdrop-blur-sm">
          <Check size={48} className="mx-auto mb-4 text-green-400" />
          <h1 className="mb-2 text-2xl font-bold">{text.decisionTitle}</h1>
          <p className="text-zinc-300">{text.decisionBody(decisionRecorded)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 to-zinc-800 px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-full bg-orange-500/10 p-4">
            <FileText size={32} className="text-orange-500" />
          </div>
          <h1 className="mb-2 text-3xl font-bold text-white">{text.title}</h1>
          <p className="text-zinc-400">{text.subtitle}</p>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
                  <User size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">{text.customer}</p>
                  <p className="font-semibold text-white">{quote.cliente_nome}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                  <Car size={20} className="text-green-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">{text.vehicle}</p>
                  <p className="font-semibold text-white">{quote.veiculo}</p>
                  <p className="text-sm text-zinc-400">{quote.placa}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/10">
                  <FileText size={20} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">OS</p>
                  <p className="font-semibold text-white">#{quote.os_numero}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/10">
                  <Calendar size={20} className="text-orange-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">{text.version}</p>
                  <p className="font-semibold text-white">v{quote.version}</p>
                </div>
              </div>
            </div>

            {currentVersion && (
              <>
                <h3 className="mb-4 text-lg font-semibold text-white">{text.items}</h3>
                <div className="mb-6 space-y-2">
                  {currentVersion.items?.map((item, index) => (
                    <div
                      key={`${item.description}-${index}`}
                      className="flex justify-between gap-4 rounded-lg border border-white/5 bg-white/5 p-3"
                    >
                      <div>
                        <p className="font-medium text-white">{item.description}</p>
                        <p className="text-sm text-zinc-400">
                          {item.item_type === 'service' ? text.service : text.part} × {item.quantity}
                        </p>
                      </div>
                      <p className="font-semibold text-white">{money(item.total)}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{text.subtotal}</span>
                    <span className="text-white">{money(currentVersion.subtotal)}</span>
                  </div>
                  {currentVersion.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-400">
                      <span>{text.discount}</span>
                      <span>-{money(currentVersion.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-white/10 pt-2 text-lg font-bold">
                    <span className="text-white">{text.total}</span>
                    <span className="text-orange-400">{money(currentVersion.total)}</span>
                  </div>
                </div>

                {currentVersion.notes && (
                  <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-4">
                    <p className="mb-2 text-sm font-semibold text-white">{text.notes}</p>
                    <p className="text-sm text-zinc-400">{currentVersion.notes}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
            <h3 className="mb-4 text-lg font-semibold text-white">{text.decision}</h3>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-white">{text.name}</label>
              <input
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder={text.namePlaceholder}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-zinc-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-white">{text.customerNotes}</label>
              <textarea
                value={customerNote}
                onChange={(event) => setCustomerNote(event.target.value)}
                placeholder={text.notesPlaceholder}
                rows={3}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-zinc-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <button
                onClick={() => handleDecision('approved')}
                disabled={submitting}
                className="flex items-center justify-center gap-2 rounded-lg bg-green-500 px-4 py-3 font-semibold text-white transition hover:bg-green-600 disabled:opacity-50"
              >
                <Check size={20} />
                {text.approve}
              </button>

              <button
                onClick={() => handleDecision('change_requested')}
                disabled={submitting}
                className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
              >
                <Send size={20} />
                {text.change}
              </button>

              <button
                onClick={() => handleDecision('rejected')}
                disabled={submitting}
                className="flex items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-3 font-semibold text-white transition hover:bg-red-600 disabled:opacity-50"
              >
                <X size={20} />
                {text.reject}
              </button>
            </div>
          </div>

          <div className="text-center text-sm text-zinc-500">
            <p>{text.validity}</p>
            <p className="mt-1">{text.product}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
